import { type Comparator, type Operator, queryBuilder } from '@inpart-io/devtools-iap';
import { GET_BY_ID_LIST, RETRIEVE_BY_QUERY } from '@inpart-io/devtools-vue';
import {
    AgreementCategoryValues,
    type IInfoGroup,
    privateContactAgreementProperties,
    privateContactAllianceProperties,
    privateContactCompanyProperties,
    privateContactLeadProperties
} from '@inpart-io/padme-iap';
import { type LocationQuery } from 'vue-router';

import { useInfoGroupFiltersHelper } from '@/components/infogroup-filters/composables/infoGroupFiltersHelper';
import { LeadDealType } from '@/constants/deal-types.constants';
import { DATE_FORMAT, endOfDay, getDateTime, getDateTimeUtc, getLocalDate, getTimeStamp, getTimeStampLocalDate, startOfDay } from '@/services/date-format';
import { isInfogroupEnabled } from '@/services/infogroups-service';
import { store } from '@/store';
import { INFO_GROUP_MODULE } from '@/store/infogroup-module';
import { GET_ALL_INFO_GROUPS } from '@/store/infogroup-module/getters';
import { INTERNAL_CONTACT_AGREEMENT_MODULE } from '@/store/internal-contact-agreement-module';
import { INTERNAL_CONTACT_ALLIANCE_MODULE } from '@/store/internal-contact-alliance-module';
import { INTERNAL_CONTACT_LEAD_MODULE } from '@/store/internal-contact-lead-module';
import { PRIVATE_CONTACT_AGREEMENT_MODULE } from '@/store/private-contact-agreement-module';
import { PRIVATE_CONTACT_ALLIANCE_MODULE } from '@/store/private-contact-alliance-module';
import { PRIVATE_CONTACT_COMPANY_MODULE } from '@/store/private-contact-company-module';
import { PRIVATE_CONTACT_LEAD_MODULE } from '@/store/private-contact-lead-module';

import { CONTACT_FILTERS_MAP, FILTERS, FILTERS_MAP, type IFilter, LIST_SETTINGS } from '../constants/filters.constants';

export class FiltersService {
    public parseIdList(object: any, field: string): number[] {
        let response: number[] = [];
        const raw: string = object[field] as string;
        if (raw?.length > 0) {
            response = raw
                .split(',')
                .map((value) => {
                    return parseInt(value, 10);
                })
                .filter((value: number) => {
                    return !isNaN(value);
                })
                .sort((a, b) => {
                    return a - b;
                });
        }

        return response;
    }

    public parseNumberList(object: any, field: string): number[] {
        let response: number[] = [];
        const raw: string = object[field] as string;
        if (raw?.length > 0) {
            response = raw
                .split(',')
                .map((value) => {
                    return parseFloat(value);
                })
                .filter((value: number) => {
                    return !isNaN(value);
                })
                .sort((a, b) => {
                    return a - b;
                });
        }
        return response;
    }

    public parseStringList(object: any, field: string): string[] {
        let response: string[] = [];
        const raw: string = object[field] as string;
        if (raw?.length > 0) {
            response = raw.split(',').sort((a, b) => {
                return a.localeCompare(b);
            });
        }
        return response;
    }

    public constructFilter(array: any[], field?: string): string {
        let response = '';
        array?.forEach((element, index) => {
            if (index > 0) {
                response += ',';
            }
            response += field !== undefined ? String(element[field]) : String(element);
        });

        return response;
    }

    public constructKey(array: Array<string | number>, keyName: string): string {
        if (array.length > 0) {
            return `${keyName}=${this.constructFilter(array)}&`;
        }
        return '';
    }

    public parseFilterString(rawFilters: string): any {
        const activeInfoGroups = store.getters[`${INFO_GROUP_MODULE}/${GET_ALL_INFO_GROUPS}`].filter((infogroup: IInfoGroup) => {
            return isInfogroupEnabled(infogroup);
        });
        const {
            getAfterOrEqualDateTypeFilters,
            getBeforeOrEqualDateTypeFilters,
            getListValueTypeFilters,
            getGreaterThanNumberTypeFilters,
            getLessThanNumberTypeFilters,
            getBooleanTypeFilters
        } = useInfoGroupFiltersHelper();
        const IGFilters =
            [
                ...getAfterOrEqualDateTypeFilters(activeInfoGroups),
                ...getBeforeOrEqualDateTypeFilters(activeInfoGroups),
                ...getListValueTypeFilters(activeInfoGroups),
                ...getGreaterThanNumberTypeFilters(activeInfoGroups),
                ...getLessThanNumberTypeFilters(activeInfoGroups),
                ...getBooleanTypeFilters(activeInfoGroups)
            ].map((IGFilter: IFilter) => {
                return IGFilter.filter;
            }) ?? [];
        const searchParams = new URLSearchParams(rawFilters);
        const filters = {};
        const filtersKeys = [...Object.values(FILTERS), ...Object.values(LIST_SETTINGS), ...IGFilters];
        searchParams.forEach((value: string, key: string) => {
            if (filtersKeys.includes(key)) {
                Object.assign(filters, { [key]: value });
            }
        });
        return filters;
    }

    public setNewParams(newParams: Record<string, string> | LocationQuery, key: string, array: any[], field: string): void {
        if (array?.length) {
            newParams[key] = this.constructFilter(array, field);
        } else {
            // TODO: [PADME-3583] [FE] Change newParam into a Map
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete newParams[key];
        }
    }

    public setNewTextParams(newParams: Record<string, string>, key: string, text: string): void {
        const trim = text?.trim();
        if (trim?.length) {
            newParams[key] = trim;
        } else {
            // TODO: [PADME-3583] [FE] Change newParam into a Map
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete newParams[key];
        }
    }

    public filterToOperator(criteria: any, skipStatus = false, skipDealType = false): Operator[] {
        const operators = [];
        const activeInfoGroups = store.getters[`${INFO_GROUP_MODULE}/${GET_ALL_INFO_GROUPS}`].filter((infogroup: IInfoGroup) => {
            return isInfogroupEnabled(infogroup);
        });
        const { getTextTypeFilters } = useInfoGroupFiltersHelper();

        for (const isInStringListFilter of FILTERS_MAP.IS_IN_STRING_LIST) {
            if (!skipStatus || isInStringListFilter.filter !== FILTERS.LEAD_STATUS) {
                const isInStringListOperator = this.filterToIsInStringOperator(isInStringListFilter.filter, isInStringListFilter.attribute, criteria);
                if (isInStringListOperator !== undefined) {
                    operators.push(isInStringListOperator);
                }
            }
        }

        for (const isInFilter of FILTERS_MAP.IS_IN) {
            const isInOperator = this.filterToIsInOperator(
                isInFilter.filter,
                isInFilter.attribute,
                criteria,
                false,
                (isInFilter as IFilter).withCriteriaName,
                (isInFilter as IFilter).withCriteriaValue
            );
            if (isInOperator !== undefined) {
                operators.push(isInOperator);
            }
        }

        for (const equalToFilter of FILTERS_MAP.EQUAL_TO) {
            const equalToOperator = this.filterToEqualToOperator(equalToFilter.filter, equalToFilter.attribute, criteria);
            if (equalToOperator !== undefined) {
                operators.push(equalToOperator);
            }
        }

        for (const equalToFilter of FILTERS_MAP.EQUAL_TO_TYPE_IS) {
            const equalToOperator = this.filterToEqualToTypeIsOperator(equalToFilter.filter, equalToFilter.attribute, criteria);
            if (equalToOperator !== undefined) {
                operators.push(equalToOperator);
            }
        }

        for (const afterOrEqualToFilter of FILTERS_MAP.AFTER_OR_EQUAL_TO) {
            const afterOrEqualToOperator = this.filterToAfterOrEqualToOperator(
                afterOrEqualToFilter.filter,
                afterOrEqualToFilter.attribute,
                afterOrEqualToFilter.utc,
                criteria,
                DATE_FORMAT.DISPLAY_DATE_SHORT,
                (afterOrEqualToFilter as IFilter).withCriteriaName,
                (afterOrEqualToFilter as IFilter).withCriteriaValue
            );
            if (afterOrEqualToOperator !== undefined) {
                operators.push(afterOrEqualToOperator);
            }
        }
        for (const beforeOrEqualToFilter of FILTERS_MAP.BEFORE_OR_EQUAL_TO) {
            const beforeOrEqualToOperator = this.filterToBeforeOrEqualToOperator(
                beforeOrEqualToFilter.filter,
                beforeOrEqualToFilter.attribute,
                beforeOrEqualToFilter.utc,
                criteria,
                DATE_FORMAT.DISPLAY_DATE_SHORT,
                (beforeOrEqualToFilter as IFilter).withCriteriaName,
                (beforeOrEqualToFilter as IFilter).withCriteriaValue
            );
            if (beforeOrEqualToOperator !== undefined) {
                operators.push(beforeOrEqualToOperator);
            }
        }

        for (const fullTextSearchFilter of FILTERS_MAP.FULL_TEXT_SEARCH) {
            const fullTextSearchComparator = this.filterToFullTextSearchComparator(fullTextSearchFilter.filter, criteria);
            const simpleTextSearchComparators = this.filterToSimpleFullTextSearchOperator(
                criteria,
                fullTextSearchFilter.filter,
                getTextTypeFilters(activeInfoGroups)
            );
            if (fullTextSearchComparator !== undefined) {
                simpleTextSearchComparators.push(fullTextSearchComparator);
            }
            const _fullTextSearchOperator = queryBuilder.op.or(...simpleTextSearchComparators);
            if (simpleTextSearchComparators.length > 0) {
                operators.push(_fullTextSearchOperator);
            }
        }

        for (const textSearchFilter of FILTERS_MAP.SIMPLE_TEXT_SEARCH) {
            const val = filtersService.parseStringList(criteria, textSearchFilter.filter)[0];

            if (val) {
                const op = queryBuilder.op.or(queryBuilder.cmp.simpleFulltextSearch(textSearchFilter.attribute, val));

                operators.push(op);
            }
        }

        const companyFilter = this.relatedCompanyFilterToOperator(criteria);
        if (companyFilter !== undefined) {
            operators.push(companyFilter);
        }
        if (!skipDealType) {
            const dealTypeOperator = this.dealTypeFilterToOperator(criteria);
            if (dealTypeOperator !== undefined) {
                operators.push(dealTypeOperator);
            }
        }

        const onlyCurrentUserBriefOperator = this.onlyCurrentUserBriefFilterToOperator(criteria);
        if (onlyCurrentUserBriefOperator !== undefined) {
            operators.push(onlyCurrentUserBriefOperator);
        }
        const isCDAOperator = this.isCDAFilterToOperator(criteria);
        if (isCDAOperator !== undefined) {
            operators.push(isCDAOperator);
        }

        return operators;
    }

    public async contactFilterToOperator(
        criteria: any,
        leadLinkedModule = PRIVATE_CONTACT_LEAD_MODULE,
        agreementLinkedModule = PRIVATE_CONTACT_AGREEMENT_MODULE,
        allianceLinkedModule = PRIVATE_CONTACT_ALLIANCE_MODULE
    ): Promise<Operator[]> {
        const operators = [];

        if (criteria[CONTACT_FILTERS_MAP.NAME.filter]?.length) {
            operators.push(queryBuilder.op.or(this.filterToFullTextSearchComparator(CONTACT_FILTERS_MAP.NAME.filter, criteria)));
        }

        if (
            (this.parseIdList(criteria, CONTACT_FILTERS_MAP.COMPANIES.filter)?.length || criteria[CONTACT_FILTERS_MAP.JOB_TITLE_SEARCH_TEXT.filter]?.length) &&
            leadLinkedModule === PRIVATE_CONTACT_LEAD_MODULE &&
            agreementLinkedModule === PRIVATE_CONTACT_AGREEMENT_MODULE &&
            allianceLinkedModule === PRIVATE_CONTACT_ALLIANCE_MODULE
        ) {
            const privateContactCompanyComparators = [];
            if (this.parseIdList(criteria, CONTACT_FILTERS_MAP.COMPANIES.filter)?.length) {
                privateContactCompanyComparators.push(queryBuilder.cmp.isIn('company', this.parseIdList(criteria, CONTACT_FILTERS_MAP.COMPANIES.filter)));
            }
            if (criteria[CONTACT_FILTERS_MAP.JOB_TITLE_SEARCH_TEXT.filter]?.length) {
                privateContactCompanyComparators.push(
                    queryBuilder.cmp.containsIgnoreCase('jobTitle', criteria[CONTACT_FILTERS_MAP.JOB_TITLE_SEARCH_TEXT.filter])
                );
            }
            operators.push(
                await this.getOperatorFromLink(privateContactCompanyComparators, PRIVATE_CONTACT_COMPANY_MODULE, privateContactCompanyProperties.DEFAULT)
            );
        }
        if (this.parseIdList(criteria, CONTACT_FILTERS_MAP.LEADS.filter)?.length) {
            const privateContactLeadComparators = [queryBuilder.cmp.isIn('lead', this.parseIdList(criteria, CONTACT_FILTERS_MAP.LEADS.filter))];
            operators.push(await this.getOperatorFromLink(privateContactLeadComparators, leadLinkedModule, privateContactLeadProperties.DEFAULT));
        }

        if (this.parseIdList(criteria, CONTACT_FILTERS_MAP.AGREEMENTS.filter)?.length) {
            const privateContactAgreementsComparators = [queryBuilder.cmp.isIn('agreement', this.parseIdList(criteria, CONTACT_FILTERS_MAP.AGREEMENTS.filter))];
            operators.push(
                await this.getOperatorFromLink(privateContactAgreementsComparators, agreementLinkedModule, privateContactAgreementProperties.DEFAULT)
            );
        }

        if (this.parseIdList(criteria, CONTACT_FILTERS_MAP.ALLIANCES.filter)?.length) {
            const privateContactAlliancesComparators = [queryBuilder.cmp.isIn('alliance', this.parseIdList(criteria, CONTACT_FILTERS_MAP.ALLIANCES.filter))];
            operators.push(await this.getOperatorFromLink(privateContactAlliancesComparators, allianceLinkedModule, privateContactAllianceProperties.DEFAULT));
        }

        // Location filtering
        const locationComparators: Comparator[] = [];
        if (this.parseIdList(criteria, CONTACT_FILTERS_MAP.COUNTRY.filter)?.length) {
            locationComparators.push(
                queryBuilder.cmp.isIn(CONTACT_FILTERS_MAP.COUNTRY.attribute, this.parseIdList(criteria, CONTACT_FILTERS_MAP.COUNTRY.filter))
            );
        }
        if (criteria[CONTACT_FILTERS_MAP.CITY.filter]?.length) {
            locationComparators.push(queryBuilder.cmp.containsIgnoreCase(CONTACT_FILTERS_MAP.CITY.attribute, criteria[CONTACT_FILTERS_MAP.CITY.filter]));
        }
        if (locationComparators.length) {
            operators.push(queryBuilder.op.or(...locationComparators));
        }

        if (
            criteria[CONTACT_FILTERS_MAP.JOB_TITLE_SEARCH_TEXT.filter]?.length &&
            leadLinkedModule === INTERNAL_CONTACT_LEAD_MODULE &&
            agreementLinkedModule === INTERNAL_CONTACT_AGREEMENT_MODULE &&
            allianceLinkedModule === INTERNAL_CONTACT_ALLIANCE_MODULE
        ) {
            operators.push(queryBuilder.op.or(queryBuilder.cmp.containsIgnoreCase('jobTitle', criteria[CONTACT_FILTERS_MAP.JOB_TITLE_SEARCH_TEXT.filter])));
        }

        if (criteria[CONTACT_FILTERS_MAP.CONTACT_TAGS.filter]?.length ?? 0) {
            operators.push(
                queryBuilder.op.or(
                    queryBuilder.cmp.isIn(
                        leadLinkedModule === INTERNAL_CONTACT_LEAD_MODULE ||
                            agreementLinkedModule === INTERNAL_CONTACT_AGREEMENT_MODULE ||
                            allianceLinkedModule === INTERNAL_CONTACT_ALLIANCE_MODULE
                            ? '@(:ContactTag:onInternalContacts)'
                            : '@(:ContactTag:onPrivateContacts)',
                        this.parseIdList(criteria, CONTACT_FILTERS_MAP.CONTACT_TAGS.filter)
                    )
                )
            );
        }

        // Date filtering
        if (CONTACT_FILTERS_MAP.CREATION_DATE_BEGIN.filter) {
            operators.push(
                this.filterToAfterOrEqualToOperator(
                    CONTACT_FILTERS_MAP.CREATION_DATE_BEGIN.filter,
                    CONTACT_FILTERS_MAP.CREATION_DATE_BEGIN.attribute,
                    CONTACT_FILTERS_MAP.CREATION_DATE_BEGIN.utc,
                    criteria
                )
            );
        }

        if (CONTACT_FILTERS_MAP.CREATION_DATE_END.filter) {
            operators.push(
                this.filterToBeforeOrEqualToOperator(
                    CONTACT_FILTERS_MAP.CREATION_DATE_END.filter,
                    CONTACT_FILTERS_MAP.CREATION_DATE_END.attribute,
                    CONTACT_FILTERS_MAP.CREATION_DATE_END.utc,
                    criteria
                )
            );
        }

        if (this.parseIdList(criteria, CONTACT_FILTERS_MAP.BUSINESS_UNIT.filter)?.length) {
            operators.push(queryBuilder.op.or(queryBuilder.cmp.isIn('businessUnit', this.parseIdList(criteria, CONTACT_FILTERS_MAP.BUSINESS_UNIT.filter))));
        }

        return operators;
    }

    public amendmentFilterToOperator(criteria: any): Operator[] {
        const operators = [];

        if (criteria[FILTERS.AMENDMENT.SEARCH_TEXT]?.length) {
            operators.push(queryBuilder.op.or(this.filterToFullTextSearchComparator(FILTERS.AMENDMENT.SEARCH_TEXT, criteria)));
        }
        if (this.parseIdList(criteria, FILTERS.AMENDMENT.AMENDED_SECTIONS)?.length) {
            operators.push(this.filterToIsInOperator(FILTERS.AMENDMENT.AMENDED_SECTIONS, 'sections', criteria));
        }

        return operators;
    }

    public obligationInstanceFilterToOperator(criteria: any): Operator[] {
        const operators = [];

        if (criteria[FILTERS.OBLIGATION_INSTANCE.SEARCH_TEXT]?.length) {
            operators.push(queryBuilder.op.or(this.filterToFullTextSearchComparator(FILTERS.OBLIGATION_INSTANCE.SEARCH_TEXT, criteria)));
        }

        return operators;
    }

    public internalContactFilterToOperator(criteria: any): Operator[] {
        const operators = [];
        if (criteria[CONTACT_FILTERS_MAP.NAME.filter]?.length) {
            operators.push(queryBuilder.op.or(queryBuilder.cmp.containsIgnoreCase('dobjPrettifiedBusinessWiseId', criteria[CONTACT_FILTERS_MAP.NAME.filter])));
        }
        return operators;
    }

    public async getOperatorFromLink(comparators: any[], module: string, properties: string): Promise<Operator> {
        const linkIdList = await store.dispatch(`${module}/${RETRIEVE_BY_QUERY}`, {
            query: queryBuilder.build(queryBuilder.op.and(...comparators)),
            properties
        });

        const linkList = store.getters[`${module}/${GET_BY_ID_LIST}`](linkIdList);

        return queryBuilder.op.or(
            queryBuilder.cmp.isIn(
                'dobjId',
                linkList.map((link: any) => {
                    return link.contact;
                }) || []
            )
        );
    }

    public filterToIsInStringOperator(filter: string, attribute: string, criteria: any): Operator {
        const values = filtersService.parseStringList(criteria, filter);
        if (values?.length > 0 && !(values.length === 1 && values[0] === 'ALL')) {
            return queryBuilder.op.or(queryBuilder.cmp.isIn(attribute, values));
        }
        return undefined;
    }

    public filterToIsInOperator(
        filter: string,
        attribute: string,
        criteria: any,
        nullAllowed = false,
        withCriteriaName: string = null,
        withCriteriaValue: string[] = null
    ): Operator {
        const values = filtersService.parseIdList(criteria, filter);
        const operators: Operator[] = [];
        if (values.length > 0) {
            const comparators: Comparator[] = [queryBuilder.cmp.isIn(attribute, values)];
            if (withCriteriaName) {
                comparators.push(queryBuilder.cmp.isIn(withCriteriaName, withCriteriaValue));
            }
            operators.push(queryBuilder.op.and(...comparators));
        }

        // For filtering purposes an empty array is the same as null so if we want cases where the array is empty we can check isNull
        if (nullAllowed && (criteria[filter] as string)?.toLowerCase().includes('null')) {
            const comparators: Comparator[] = [queryBuilder.cmp.isNull(attribute)];
            if (withCriteriaName) {
                comparators.push(queryBuilder.cmp.isIn(withCriteriaName, withCriteriaValue));
            }
            operators.push(queryBuilder.op.and(...comparators));
        }
        if (operators.length > 0) {
            return queryBuilder.op.or(...operators);
        }
    }

    public filterToEqualToOperator(filter: string, attribute: string, criteria: any): Operator {
        const value = filtersService.parseIdList(criteria, filter)[0];
        if (value !== null && value !== undefined && value > 0) {
            return queryBuilder.op.or(queryBuilder.cmp.equalTo(attribute, value));
        }
    }

    public filterToContainsIgnoreCase(filter: string, attribute: string, criteria: any): Operator {
        const values = filtersService.parseStringList(criteria, filter);
        if (values?.length > 0 && !(values.length === 1 && values[0] === 'ALL')) {
            const comparators: Comparator[] = [];
            for (const value of values) {
                comparators.push(queryBuilder.cmp.containsIgnoreCase(attribute, value));
            }
            return queryBuilder.op.or(...comparators);
        }
    }

    public filterToEqualToTypeIsOperator(filter: string, attribute: string, criteria: any): Operator {
        const valuesInURL = filtersService.parseStringList(criteria, filter)[0];
        const values = valuesInURL?.split('_') ?? [];
        if (values.length > 0) {
            const comparators = [queryBuilder.cmp.equalTo(attribute, Number(values[0]))];
            if (values[1] !== undefined) {
                comparators.push(queryBuilder.cmp.typeIs(attribute, values[1]));
            }
            return queryBuilder.op.and(...comparators);
        }
    }

    public filterToEqualToStringOperator(
        filter: string,
        attribute: string,
        criteria: any,
        withCriteriaName: string = null,
        withCriteriaValue: string[] = null
    ): Operator {
        const value = filtersService.parseStringList(criteria, filter)[0];
        if (value !== null && value !== undefined) {
            const comparators: Comparator[] = [queryBuilder.cmp.equalTo(attribute, value)];
            if (withCriteriaName) {
                comparators.push(queryBuilder.cmp.isIn(withCriteriaName, withCriteriaValue));
            }
            return queryBuilder.op.or(...comparators);
        }
    }

    public filterToEqualOrGreaterThanToOperator(
        filter: string,
        attribute: string,
        criteria: any,
        withCriteriaName: string = null,
        withCriteriaValue: string[] = null
    ): Operator {
        const value = filtersService.parseNumberList(criteria, filter)[0];
        if (typeof value === 'number') {
            const comparators: Comparator[] = [queryBuilder.cmp.greaterThanOrEqualTo(attribute, value)];
            if (withCriteriaName) {
                comparators.push(queryBuilder.cmp.isIn(withCriteriaName, withCriteriaValue));
            }
            return queryBuilder.op.and(...comparators);
        }
    }

    public filterToEqualOrLessThanToOperator(
        filter: string,
        attribute: string,
        criteria: any,
        withCriteriaName: string = null,
        withCriteriaValue: string[] = null
    ): Operator {
        const value = filtersService.parseNumberList(criteria, filter)[0];
        if (typeof value === 'number') {
            const comparators: Comparator[] = [queryBuilder.cmp.lessThanOrEqualTo(attribute, value)];
            if (withCriteriaName) {
                comparators.push(queryBuilder.cmp.isIn(withCriteriaName, withCriteriaValue));
            }
            return queryBuilder.op.and(...comparators);
        }
    }

    public filterToBeforeOrEqualToOperator(
        filter: string,
        attribute: string,
        utc: boolean,
        criteria: any,
        format = DATE_FORMAT.DISPLAY_DATE_SHORT,
        withCriteriaName: string = null,
        withCriteriaValue: string[] = null
    ): Operator {
        const value = criteria[filter];
        if (value !== null && value !== undefined && value.length > 0) {
            const adjustedDate = endOfDay(utc ? getDateTimeUtc(value, format) : getDateTime(value, format));
            const comparators = [queryBuilder.cmp.beforeOrEqualTo(attribute, getTimeStamp(adjustedDate))];
            if (withCriteriaName) {
                comparators.push(queryBuilder.cmp.isIn(withCriteriaName, withCriteriaValue));
            }

            return queryBuilder.op.and(...comparators);
        }
    }

    public filterToAfterOrEqualToOperator(
        filter: string,
        attribute: string,
        utc: boolean,
        criteria: any,
        format = DATE_FORMAT.DISPLAY_DATE_SHORT,
        withCriteriaName: string = null,
        withCriteriaValue: string[] = null
    ): Operator {
        const value = criteria[filter];
        if (value !== null && value !== undefined && value.length > 0) {
            const adjustedDate = startOfDay(utc ? getDateTimeUtc(value, format) : getDateTime(value, format));
            const comparators = [queryBuilder.cmp.afterOrEqualTo(attribute, getTimeStamp(adjustedDate))];
            if (withCriteriaName) {
                comparators.push(queryBuilder.cmp.isIn(withCriteriaName, withCriteriaValue));
            }
            return queryBuilder.op.and(...comparators);
        }
    }

    public filterToBeforeOrEqualToLocalDateOperator(filter: string, attribute: string, criteria: any): any {
        const value = criteria[filter];
        if (value !== null && value !== undefined && value.length > 0) {
            const adjustedDate = getLocalDate(value);
            return queryBuilder.op.or(queryBuilder.cmp.beforeOrEqualTo(attribute, getTimeStampLocalDate(adjustedDate)));
        }
    }

    public filterToAfterOrEqualToLocalDateOperator(filter: string, attribute: string, criteria: any): any {
        const value = criteria[filter];
        if (value !== null && value !== undefined && value.length > 0) {
            const adjustedDate = getLocalDate(value);
            return queryBuilder.op.or(queryBuilder.cmp.afterOrEqualTo(attribute, getTimeStampLocalDate(adjustedDate)));
        }
    }

    public filterToFullTextSearchComparator(filter: string, criteria: any): Comparator {
        const value = filtersService.parseStringList(criteria, filter)[0];
        if (value !== null && value !== undefined && value.length > 0) {
            return queryBuilder.cmp.fulltextSearch(value);
        }
    }

    public filterToSimpleFullTextSearchOperator(criteria: any, searchedTextFilter: string, infoGroupTextFilters: IFilter[]): Comparator[] {
        const _comparators: Comparator[] = [];
        infoGroupTextFilters.forEach((filter: IFilter) => {
            const value = filtersService.parseStringList(criteria, searchedTextFilter)[0];
            if (value !== null && value !== undefined && value.length > 0) {
                _comparators.push(queryBuilder.cmp.simpleFulltextSearch(filter.attribute, value));
            }
        });
        return _comparators;
    }

    public relatedCompanyFilterToOperator(criteria: any): Operator | undefined {
        const COMPANY_ATTRIBUTE = 'company';
        const RELATED_COMPANIES_ATTRIBUTE = '@(:LeadCompany:lead).company';
        const values = filtersService.parseIdList(criteria, FILTERS.RELATED_COMPANIES);
        const comparators = [];
        if (values.length > 0) {
            comparators.push(queryBuilder.cmp.isIn(COMPANY_ATTRIBUTE, values));
            comparators.push(queryBuilder.cmp.isIn(RELATED_COMPANIES_ATTRIBUTE, values));
        }
        return comparators.length > 0 ? queryBuilder.op.or(...comparators) : undefined;
    }

    public dealTypeFilterToOperator(criteria: any): Operator | undefined {
        const DEAL_TYPE_ATTRIBUTE = 'dealType';
        const DEAL_STAGE_ATTRIBUTE = 'typeStageKey';
        const oldLeadDealTypeId = -1000;
        const comparators = [];
        let valuesOfDealType = filtersService.parseIdList(criteria, FILTERS.WORKFLOW_TYPES).map((id: number) => {
            return id === oldLeadDealTypeId ? LeadDealType.dobjId : id;
        });
        const appliedStages = filtersService.parseStringList(criteria, FILTERS.WORKFLOW_STAGES);
        if (valuesOfDealType.length > 0 && appliedStages.length > 0) {
            const dealTypesFromStages = appliedStages.map((appliedStage: string) => {
                return Number(appliedStage.split('_')[0]);
            });
            valuesOfDealType = valuesOfDealType.filter((dobjId: number) => {
                return !dealTypesFromStages.includes(dobjId);
            });
        }
        if (valuesOfDealType.length > 0) {
            const defaultLeadDealType = valuesOfDealType.filter((dealTypeId: number) => {
                return dealTypeId === LeadDealType.dobjId;
            })[0];
            if (defaultLeadDealType !== undefined) {
                comparators.push(queryBuilder.cmp.isNull(DEAL_TYPE_ATTRIBUTE));
            }
            comparators.push(queryBuilder.cmp.isIn(DEAL_TYPE_ATTRIBUTE, valuesOfDealType));
        }
        if (appliedStages.length > 0) {
            comparators.push(queryBuilder.cmp.isIn(DEAL_STAGE_ATTRIBUTE, appliedStages));
        }
        return comparators.length > 0 ? queryBuilder.op.or(...comparators) : undefined;
    }

    public onlyCurrentUserBriefFilterToOperator(criteria: any): Operator {
        const onlyCurrentUserBriefValue = filtersService.parseIdList(criteria, FILTERS.ONLY_CURRENT_USER_BRIEF);
        if (onlyCurrentUserBriefValue.length > 0) {
            return queryBuilder.op.or(
                queryBuilder.cmp.isIn('brief.accessControlInfo.functionalOwner', onlyCurrentUserBriefValue),
                queryBuilder.cmp.isIn('accessControlInfo.authorizedUsers.targetId', onlyCurrentUserBriefValue)
            );
        }
    }

    public isCDAFilterToOperator(criteria: any): Operator {
        const isCDAValue = criteria[FILTERS.IS_CDA];
        if (isCDAValue === true.toString()) {
            return queryBuilder.op.or(queryBuilder.cmp.equalTo('@(:Agreement:leads).category', AgreementCategoryValues.CDA));
        } else if (isCDAValue === false.toString()) {
            return queryBuilder.op.not(queryBuilder.cmp.equalTo('@(:Agreement:leads).category', AgreementCategoryValues.CDA));
        }
        return undefined;
    }
}

export const filtersService = new FiltersService();
